import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowRight, RotateCcw, Award, Compass, Gauge, ClipboardList } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCourse, useProgress, currentTierData, TIER_COPY, SHOW_READINESS } from "@/lib/hooks";
import { PageShell } from "@/components/AppShell";
import { TierLadder } from "@/components/TierLadder";
import { Loader } from "@/components/ProtectedRoute";
import { ExecutiveHome } from "@/components/executive/ExecutiveHome";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { tierLevel, t as tr } from "@/lib/i18n";

const Banner = ({ tone, icon: Icon, title, children, testId }) => (
  <div data-testid={testId} className={`flex gap-4 border-l-4 bg-card p-5 ${tone}`}>
    <Icon className="mt-0.5 h-5 w-5 shrink-0" />
    <div><div className="font-heading font-bold">{title}</div><div className="mt-1 text-sm text-muted-foreground">{children}</div></div>
  </div>
);

const nextAction = (course, t, progress) => {
  if (!progress.placement_level) return { label: "Take the 12-question placement quiz", diag: true };
  if (t.status === "capstone_pending") return { label: "Start the Final Applied Project", to: "/capstone" };
  for (const m of course.modules) {
    const s = t.modules.find((x) => x.module_id === m.id);
    if (s.passed) continue;
    const les = m.lessons.find((l) => !t.lessons_done.includes(l.id));
    if (les) return { label: `${les.kind === "lab" ? "Try a plant decision" : "Continue"}: ${les.title}`, to: `/lessons/${les.id}`, module: m };
    return { label: `Take the ${m.id} certified check`, to: `/modules/${m.id}`, module: m };
  }
  return { label: "View your certificates", to: "/certificates" };
};

function SkipFoundation() {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const skip = async () => {
    setBusy(true);
    try {
      await api.post("/progress/skip-foundation");
      await qc.invalidateQueries({ queryKey: ["progress"] });
      toast.success("Foundation skipped. You are now on Practitioner.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div data-testid="skip-foundation-card" className="mt-4 flex flex-col gap-4 rounded-xl border border-brand-accent/50 bg-brand-accent/5 p-5 sm:flex-row sm:items-center sm:justify-between">
      <div>
        <div className="font-heading text-lg font-bold">You can skip Foundation</div>
        <p className="mt-1 text-sm text-muted-foreground">{SHOW_READINESS ? "Your readiness result or placement quiz shows you are ready." : "Your placement quiz shows you are ready."} Skipping opens Practitioner now. You will not receive a Foundation certificate.</p>
      </div>
      <AlertDialog>
        <AlertDialogTrigger asChild><button data-testid="skip-foundation-button" disabled={busy} className="shrink-0 rounded-full bg-brand-accent px-5 py-2.5 text-sm font-semibold text-brand-accent-fg disabled:opacity-50">Skip to Practitioner</button></AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Skip Foundation?</AlertDialogTitle><AlertDialogDescription>You go straight to Practitioner. There is no Foundation certificate for skipped levels. You can still open Foundation lessons later.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel data-testid="skip-foundation-cancel">Cancel</AlertDialogCancel><AlertDialogAction data-testid="skip-foundation-confirm" onClick={skip}>Skip Foundation</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  return user.track === "executive" ? <ExecutiveHome /> : <StrategistDashboard />;
}

function StrategistDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { data: progress } = useProgress();
  const { data: course } = useCourse();
  const { data: readiness } = useQuery({ queryKey: ["readiness"], queryFn: async () => (await api.get("/readiness")).data, enabled: SHOW_READINESS });
  if (!progress || !course) return <Loader />;
  const t = currentTierData(progress);
  const act = nextAction(course, t, progress);
  const day = act.module?.day || (act.diag || !t.lessons_done.length ? 1 : 3);
  const latest = readiness?.runs?.[readiness.runs.length - 1]?.report;
  const go = async () => {
    if (!act.diag) return navigate(act.to);
    try {
      const { data } = await api.post("/quiz/diagnostic/start");
      navigate(`/quiz/${data.attempt_id}`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  return (
    <PageShell>
      <div className="overline-label">Day {day} of 3 · {["See", "Design", "Deploy"][day - 1]}</div>
      <div className="overline-label mt-1">{tierLevel(TIER_COPY[t.tier].label)}</div>
      <h1 data-testid="dashboard-greeting" className="mt-2 font-heading text-3xl font-black tracking-tight sm:text-4xl">Hello, {user.name.split(" ")[0]}.</h1>
      <div data-testid="dashboard-next-action" className="mt-8 flex flex-col gap-5 rounded-xl border-2 border-brand-primary bg-card p-6 sm:flex-row sm:items-center sm:justify-between md:p-8">
        <div><div className="text-sm font-semibold text-brand-primary">Your next step</div><div className="mt-1 font-heading text-xl font-bold sm:text-2xl">{act.label}</div></div>
        <button onClick={go} data-testid="dashboard-resume-button" className="inline-flex shrink-0 items-center justify-center gap-2 rounded-full bg-brand-primary px-6 py-3 font-semibold text-brand-primary-fg transition-opacity hover:opacity-90">{act.diag ? <Compass className="h-4 w-4" /> : null}{act.diag ? "Start" : "Continue"} <ArrowRight className="h-4 w-4" /></button>
      </div>
      <div className="mt-6 space-y-3">
        {t.status === "repeat_course" && <Banner testId="repeat-course-banner" tone="border-brand-action" icon={RotateCcw} title={`Repeat course · cycle ${t.cycle}`}>A certified score fell below 80%. Re-read the lessons from Module 1, then take fresh certified checks with new questions.</Banner>}
        {t.status === "capstone_pending" && <Banner testId="capstone-pending-banner" tone="border-brand-primary" icon={Award} title="All eight Strategist modules passed">Your Final Applied Project is open.</Banner>}
      </div>
      <div data-testid="dashboard-you-are-here" className="mt-8">
        <div className="overline-label mb-2">You are here · {t.percent}% of this level</div>
        <div className="flex gap-1">{course.modules.map((m) => { const s = t.modules.find((x) => x.module_id === m.id); return <Link key={m.id} to={`/modules/${m.id}`} title={m.title} className={`h-3 flex-1 rounded-full ${s.passed ? "bg-brand-accent" : act.module?.id === m.id ? "bg-brand-primary" : "bg-muted"}`} />; })}</div>
        <div className="mt-1 flex justify-between font-mono text-[10px] text-muted-foreground">{course.modules.map((m) => <span key={m.id}>{m.id}</span>)}</div>
      </div>
      <div className="mt-10">
        {SHOW_READINESS && <Link to="/readiness" data-testid="dashboard-readiness-link" className="mb-4 flex items-start gap-4 rounded-lg border border-border bg-card p-5 transition-colors hover:border-brand-primary">
          <Gauge className="mt-1 h-5 w-5 shrink-0 text-brand-data" />
          <div>
            <div className="font-heading text-lg font-bold">{latest ? latest.headline : "Find out where your plant is today"}</div>
            <p className="mt-1 text-sm text-muted-foreground">{latest ? `Next 30 days: ${latest.route.d30}` : "Optional · 12 statements · about 3 minutes · private"}</p>
          </div>
        </Link>}
        <Link to="/project" data-testid="dashboard-project-link" className="flex items-start gap-4 rounded-lg border border-border bg-card p-5 transition-colors hover:border-brand-primary">
          <ClipboardList className="mt-1 h-5 w-5 shrink-0 text-brand-accent" />
          <div>
            <div className="font-heading text-lg font-bold">{tr("Your plant project")}</div>
            <p className="mt-1 text-sm text-muted-foreground">{tr("project-dash") === "project-dash" ? "Each lesson ends with one step for your own plant. See all the steps you have done so far." : tr("project-dash")}</p>
          </div>
        </Link>
      </div>
      <div className="mt-10"><TierLadder tiers={progress.tiers} current={progress.current_tier} /></div>
      {progress.skip_foundation_eligible && progress.current_tier === "foundation" && <SkipFoundation />}
      {progress.placement_level && <p data-testid="diagnostic-result" className="mt-4 text-xs text-muted-foreground">Placement: {TIER_COPY[progress.placement_level].label} ({progress.diagnostic_score}%) — sets your starting difficulty inside each level.</p>}
    </PageShell>
  );
}
